// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
} from "@mui/material"
import type { Contact, ContactFormData } from "@crm-src/lib/contacts-api"
import { INDUSTRY_OPTIONS } from "@crm-src/lib/industries"

interface EditContactDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  formData: ContactFormData
  onFormDataChange: (data: ContactFormData) => void
  onSubmit: () => void
  /** The contact being edited — drives the "synced from a prospect/customer"
   * hint + read-only core fields for mirrored rows. */
  contact?: Contact | null
}

export function EditContactDialog({
  open,
  onOpenChange,
  formData,
  onFormDataChange,
  onSubmit,
  contact,
}: EditContactDialogProps) {
  // Mirrored contacts are a one-way projection of a person on a prospect/customer.
  // Their core details are managed at the source (and would be overwritten by the
  // next sync), so we lock those fields here — but the industry override + notes +
  // active state are directory-local and stay editable.
  const isMirrored = contact?.source === "mirrored"
  return (
    <Dialog open={open} onClose={() => onOpenChange(false)} maxWidth="md" fullWidth>
      <DialogTitle>Edit Contact</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Update the contact information below
        </DialogContentText>
        {isMirrored && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Synced from {contact?.company || "a prospect/customer"}. Name, email and other core
            details are managed on that record — edit them there. You can still set the industry,
            notes and status here.
          </Alert>
        )}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 2 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              id="edit-name"
              label="Name"
              size="small"
              fullWidth
              disabled={isMirrored}
              value={formData.name}
              onChange={(e) => onFormDataChange({ ...formData, name: e.target.value })}
            />
            <TextField
              id="edit-surname"
              label="Surname"
              size="small"
              fullWidth
              disabled={isMirrored}
              value={formData.surname}
              onChange={(e) => onFormDataChange({ ...formData, surname: e.target.value })}
            />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              id="edit-company"
              label="Company"
              size="small"
              fullWidth
              disabled={isMirrored}
              value={formData.company}
              onChange={(e) => onFormDataChange({ ...formData, company: e.target.value })}
            />
            <TextField
              id="edit-job_title"
              label="Job Title"
              size="small"
              fullWidth
              disabled={isMirrored}
              value={formData.job_title}
              onChange={(e) => onFormDataChange({ ...formData, job_title: e.target.value })}
            />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              id="edit-email"
              label="Email"
              type="email"
              size="small"
              fullWidth
              disabled={isMirrored}
              value={formData.email}
              onChange={(e) => onFormDataChange({ ...formData, email: e.target.value })}
            />
            <TextField
              id="edit-phone"
              label="Phone"
              size="small"
              fullWidth
              disabled={isMirrored}
              value={formData.phone}
              onChange={(e) => onFormDataChange({ ...formData, phone: e.target.value })}
            />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              id="edit-mobile"
              label="Mobile"
              size="small"
              fullWidth
              disabled={isMirrored}
              value={formData.mobile}
              onChange={(e) => onFormDataChange({ ...formData, mobile: e.target.value })}
            />
            <TextField
              id="edit-industry"
              label="Industry"
              select
              size="small"
              fullWidth
              value={formData.industry ?? ""}
              onChange={(e) => onFormDataChange({ ...formData, industry: e.target.value })}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {INDUSTRY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <TextField
            id="edit-address"
            label="Address"
            size="small"
            fullWidth
            disabled={isMirrored}
            value={formData.address}
            onChange={(e) => onFormDataChange({ ...formData, address: e.target.value })}
          />
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}>
            <TextField
              id="edit-city"
              label="City"
              size="small"
              fullWidth
              disabled={isMirrored}
              value={formData.city}
              onChange={(e) => onFormDataChange({ ...formData, city: e.target.value })}
            />
            <TextField
              id="edit-state"
              label="State"
              size="small"
              fullWidth
              disabled={isMirrored}
              value={formData.state}
              onChange={(e) => onFormDataChange({ ...formData, state: e.target.value })}
            />
            <TextField
              id="edit-postal_code"
              label="Postal Code"
              size="small"
              fullWidth
              disabled={isMirrored}
              value={formData.postal_code}
              onChange={(e) => onFormDataChange({ ...formData, postal_code: e.target.value })}
            />
            <TextField
              id="edit-country"
              label="Country"
              size="small"
              fullWidth
              disabled={isMirrored}
              value={formData.country}
              onChange={(e) => onFormDataChange({ ...formData, country: e.target.value })}
            />
          </Box>
          <TextField
            id="edit-notes"
            label="Notes"
            size="small"
            fullWidth
            multiline
            minRows={3}
            value={formData.notes}
            onChange={(e) => onFormDataChange({ ...formData, notes: e.target.value })}
          />
          <FormControlLabel
            control={
              <Switch
                id="edit-active"
                checked={formData.active}
                onChange={(e) => onFormDataChange({ ...formData, active: e.target.checked })}
              />
            }
            label="Active"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onSubmit}>Update Contact</Button>
      </DialogActions>
    </Dialog>
  )
}
